import { DateTime } from 'luxon';
import { naivelyGetOrdinalSuffix } from '~utils/date-helper';

export const formatWeekdayMonthOrdinal = (dateIso: string): string => {
  const dayDate = DateTime.fromISO(dateIso);
  return `${dayDate.toFormat('cccc, LLLL')} ${naivelyGetOrdinalSuffix(dayDate.day)}`;
};

export const formatRelativeMonthOrdinal = (dateIso: string): string => {
  const day = DateTime.fromISO(dateIso);
  const now = DateTime.now();
  const suffix = `${day.toFormat('LLLL')} ${naivelyGetOrdinalSuffix(day.day)}`;

  if (day.hasSame(now, 'day')) return `Today, ${suffix}`;
  if (day.hasSame(now.minus({ days: 1 }), 'day')) return `Yesterday, ${suffix}`;
  if (day.hasSame(now.plus({ days: 1 }), 'day')) return `Tomorrow, ${suffix}`;

  return `${day.toFormat('cccc, LLLL')} ${naivelyGetOrdinalSuffix(day.day)}`;
};

/**
 * Returns a contextual day label:
 *  - "Today" / "Yesterday" / "Tomorrow" for those relative days
 *  - "Monday" (just the name) if the date falls within the current week
 *  - "Monday, 13th" if the date is in a past or future week
 */
export const formatRelativeDayLabel = (dateIso: string): string => {
  const day = DateTime.fromISO(dateIso);
  const now = DateTime.now();

  if (day.hasSame(now, 'day')) return 'Today';
  if (day.hasSame(now.minus({ days: 1 }), 'day')) return 'Yesterday';
  if (day.hasSame(now.plus({ days: 1 }), 'day')) return 'Tomorrow';

  if (day.hasSame(now, 'week')) return day.toFormat('cccc');

  return `${day.toFormat('cccc')}, ${naivelyGetOrdinalSuffix(day.day)}`;
};

/**
 * Formats a week date range, e.g. "March 9th – March 15th"
 * or "February 27th – March 5th" when months differ.
 */
export const formatWeekRange = (startIso: string, endIso: string): string => {
  const start = DateTime.fromISO(startIso);
  const end = DateTime.fromISO(endIso);
  const startStr = `${start.toFormat('LLLL')} ${naivelyGetOrdinalSuffix(start.day)}`;
  const endStr = `${end.toFormat('LLLL')} ${naivelyGetOrdinalSuffix(end.day)}`;
  return `${startStr} – ${endStr}`;
};

/**
 * Formats submit-day tooltip text for mixed day combinations.
 * Examples:
 * - Monday
 * - Monday & Tuesday
 * - Monday to Wednesday
 * - Monday to Wednesday & Friday
 */
export const formatSubmitDaysTooltip = (dateIsos: string[]): string | undefined => {
  const dates = dateIsos
    .map(dateIso => DateTime.fromISO(dateIso).startOf('day'))
    .filter(date => date.isValid)
    .sort((a, b) => a.toMillis() - b.toMillis());

  if (dates.length === 0) return undefined;

  const uniqueDates: DateTime[] = [];
  const seen = new Set<string>();
  dates.forEach(date => {
    const iso = date.toISODate();
    if (!iso || seen.has(iso)) return;
    seen.add(iso);
    uniqueDates.push(date);
  });

  if (uniqueDates.length === 1) return uniqueDates[0].toFormat('cccc');
  if (uniqueDates.length === 2) {
    return `${uniqueDates[0].toFormat('cccc')} & ${uniqueDates[1].toFormat('cccc')}`;
  }

  return uniqueDates.map(d => d.toFormat('ccc')).join(', ');
};

/**
 * Resolves which tooltip the revert affordance should show.
 *
 * `useSummaryProps` sets `lockTooltip` only when the relevant day(s) are locked;
 * otherwise it is `undefined`. `Main` separately computes a submit-dates tooltip
 * (the list of accepted days) and passes it explicitly. The lock message must win
 * on a locked day; without this precedence the explicit submit-dates prop shadows
 * the lock tooltip in day view, so a locked day disables the button but shows the
 * wrong text.
 */
export const resolveRevertActionTooltip = (
  lockTooltip: string | undefined,
  submitDatesTooltip: string | undefined
): string | undefined => lockTooltip ?? submitDatesTooltip;

/**
 * Compact duration for the abridged timeline's collapsed stretches, e.g.
 * "2h 30m", "45m", "3h". Whole minutes only — a gap band is a coarse label,
 * not a timesheet figure. `formattedDuration(_, 'traditional')` in
 * `~utils/durations` always pads to "0h 45m", which reads wrong in a sentence.
 */
export const formatGapDuration = (minutes: number): string => {
  const totalMinutes = Math.max(0, Math.round(minutes));
  const wholeHours = Math.floor(totalMinutes / 60);
  const remainderMinutes = totalMinutes % 60;

  if (wholeHours === 0) return `${remainderMinutes}m`;
  if (remainderMinutes === 0) return `${wholeHours}h`;
  return `${wholeHours}h ${remainderMinutes}m`;
};
